"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "@/components/ui/Link";
import { usePathname } from "next/navigation";
import { Logo } from "@/components/brand/Logo";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { MegaMenuPanel } from "@/components/layout/MegaMenuPanel";
import { MegaMenuIcon } from "@/components/layout/MegaMenuIcon";
import { buildDesktopNav } from "@/lib/content/navigation";
import type { DesktopNavEntry } from "@/lib/content/navigation";
import type { NavItem } from "@/lib/content/types";
import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { stripLocaleFromPathname } from "@/lib/i18n";

type HeaderProps = {
  navigation: NavItem[];
  ctaLabel?: string;
};

function isActivePath(pathname: string, href: string) {
  const barePath = stripLocaleFromPathname(pathname);
  if (href.startsWith("/#")) {
    return barePath === "/";
  }
  const bareHref = stripLocaleFromPathname(href);
  return (
    barePath === bareHref ||
    barePath.startsWith(`${bareHref.endsWith("/") ? bareHref : `${bareHref}/`}`)
  );
}

function findMegaPanel(nav: DesktopNavEntry[], label: string | null) {
  if (!label) return undefined;
  const entry = nav.find((item) => item.type === "mega" && item.label === label);
  return entry?.type === "mega" ? entry.panel : undefined;
}

export function Header({
  navigation,
  ctaLabel,
}: HeaderProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMega, setActiveMega] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navId = useId();

  const desktopNav = useMemo(
    () => buildDesktopNav(navigation, { omitContact: true }),
    [navigation],
  );

  const activePanel = findMegaPanel(desktopNav, activeMega);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openMega = useCallback(
    (key: string) => {
      clearCloseTimer();
      setActiveMega(key);
    },
    [clearCloseTimer],
  );

  const scheduleCloseMega = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setActiveMega(null), 140);
  }, [clearCloseTimer]);

  const closeMega = useCallback(() => {
    clearCloseTimer();
    setActiveMega(null);
  }, [clearCloseTimer]);

  useEffect(() => {
    closeMega();
    setMobileOpen(false);
    setMobileExpanded(null);
  }, [pathname, closeMega]);

  useEffect(() => {
    if (!activeMega && !mobileOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMega();
        setMobileOpen(false);
      }
    };

    const onPointerDown = (event: MouseEvent) => {
      if (!activeMega) return;
      const target = event.target as Node | null;
      const shell = document.querySelector("[data-header-shell]");
      if (shell && target && shell.contains(target)) return;
      closeMega();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [activeMega, mobileOpen, closeMega]);

  useEffect(() => {
    return () => clearCloseTimer();
  }, [clearCloseTimer]);

  return (
    <div className="sticky top-0 z-50" data-header-shell>
      <header className="relative z-50 bg-background/80 backdrop-blur-md">
        <Container as="div" className="flex h-16 items-center justify-between md:h-20">
          <Logo />

          <nav
            className="hidden items-center gap-1 lg:flex"
            aria-label="Main navigation"
            onMouseLeave={scheduleCloseMega}
          >
            {desktopNav.map((entry) => {
              const active = isActivePath(pathname, entry.href);
              const megaOpen = activeMega === entry.label;

              if (entry.type === "link") {
                return (
                  <Link
                    key={`${entry.href}-${entry.label}`}
                    href={entry.href}
                    onFocus={closeMega}
                    className={`px-3 py-2 text-sm transition-colors hover:text-foreground ${
                      active ? "text-foreground" : "text-muted"
                    }`}
                  >
                    {entry.label}
                  </Link>
                );
              }

              const triggerId = `${navId}-${entry.label}`;

              return (
                <div
                  key={`${entry.href}-${entry.label}`}
                  className="relative"
                  onMouseEnter={() => openMega(entry.label)}
                >
                  <button
                    type="button"
                    id={triggerId}
                    aria-expanded={megaOpen}
                    aria-haspopup="true"
                    aria-controls="mega-menu-panel"
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm transition-colors hover:text-foreground ${
                      active || megaOpen ? "text-foreground" : "text-muted"
                    }`}
                    onClick={() =>
                      megaOpen ? closeMega() : openMega(entry.label)
                    }
                    onFocus={() => openMega(entry.label)}
                  >
                    {entry.label}
                    <svg
                      className={`h-3 w-3 transition-transform duration-200 ${
                        megaOpen ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path d="M2.5 4.5 6 8l3.5-3.5" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <LanguageSwitcher />
            {ctaLabel ? (
              <Button href="/contact" variant="secondary">
                {ctaLabel}
              </Button>
            ) : null}
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSwitcher />
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center text-foreground"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              onClick={() => setMobileOpen((value) => !value)}
            >
              <span className="sr-only">Menu</span>
              <div className="flex flex-col gap-1.5">
                <span
                  className={`block h-px w-5 bg-foreground transition-transform ${mobileOpen ? "translate-y-[3.5px] rotate-45" : ""}`}
                />
                <span
                  className={`block h-px w-5 bg-foreground transition-opacity ${mobileOpen ? "opacity-0" : ""}`}
                />
                <span
                  className={`block h-px w-5 bg-foreground transition-transform ${mobileOpen ? "-translate-y-[3.5px] -rotate-45" : ""}`}
                />
              </div>
            </button>
          </div>
        </Container>
      </header>

      {/* Sibling of the blurred header bar so backdrop-filter can sample the page */}
      <MegaMenuPanel
        panel={activePanel}
        open={Boolean(activePanel)}
        onClose={closeMega}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleCloseMega}
      />

      {mobileOpen && (
        <nav
          id="mobile-nav"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto border-t border-border bg-background/80 backdrop-blur-md lg:hidden"
          aria-label="Mobile navigation"
        >
          <Container className="flex flex-col py-4">
            {desktopNav.map((entry) => {
              if (entry.type === "link") {
                return (
                  <Link
                    key={`${entry.href}-${entry.label}`}
                    href={entry.href}
                    className="py-3 text-lg text-muted transition-colors hover:text-foreground"
                    onClick={() => setMobileOpen(false)}
                  >
                    {entry.label}
                  </Link>
                );
              }

              const expanded = mobileExpanded === entry.label;

              return (
                <div
                  key={`${entry.href}-${entry.label}`}
                  className="border-b border-border last:border-b-0"
                >
                  <button
                    type="button"
                    className="flex w-full items-center justify-between py-3 text-left text-lg text-muted transition-colors hover:text-foreground"
                    aria-expanded={expanded}
                    onClick={() =>
                      setMobileExpanded(expanded ? null : entry.label)
                    }
                  >
                    {entry.label}
                    <svg
                      className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path d="M2.5 4.5 6 8l3.5-3.5" />
                    </svg>
                  </button>

                  {expanded && (
                    <div className="pb-4">
                      <Link
                        href={entry.panel.featured.href}
                        className="mb-3 block bg-white/[0.03] p-4 ring-1 ring-inset ring-border-strong"
                        onClick={() => setMobileOpen(false)}
                      >
                        <p className="text-xs uppercase tracking-[0.22em] text-muted">
                          {entry.panel.eyebrow}
                        </p>
                        <p className="mt-2 text-base font-medium text-foreground">
                          {entry.panel.featured.title}
                        </p>
                        <p className="mt-1 text-sm text-muted">
                          {entry.panel.featured.description}
                        </p>
                      </Link>
                      {entry.panel.links.length > 6 ? (
                        <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {entry.panel.links.map((item, index) => (
                            <li key={`${item.href}-${item.label}`}>
                              <Link
                                href={item.href}
                                className="block rounded-sm px-1 py-2 text-muted transition-colors hover:text-foreground"
                                onClick={() => setMobileOpen(false)}
                              >
                                <span className="flex items-baseline gap-1.5">
                                  <span className="font-mono text-[10px] text-muted/50">
                                    {String(index + 1).padStart(2, "0")}
                                  </span>
                                  <span className="text-sm text-foreground">
                                    {item.label}
                                  </span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <ul className="flex flex-col gap-1">
                          {entry.panel.links.map((item) => (
                            <li key={`${item.href}-${item.label}`}>
                              <Link
                                href={item.href}
                                className="flex gap-3 px-1 py-2.5 text-muted transition-colors hover:text-foreground"
                                onClick={() => setMobileOpen(false)}
                              >
                                {item.icon && (
                                  <span className="mt-0.5 text-muted">
                                    <MegaMenuIcon
                                      id={item.icon}
                                      className="h-4 w-4"
                                    />
                                  </span>
                                )}
                                <span>
                                  <span className="block text-base text-foreground">
                                    {item.label}
                                  </span>
                                  <span className="mt-0.5 block text-sm text-muted">
                                    {item.description}
                                  </span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {ctaLabel ? (
              <div className="mt-4 pb-2">
                <Button href="/contact" variant="secondary" className="w-full">
                  {ctaLabel}
                </Button>
              </div>
            ) : null}
          </Container>
        </nav>
      )}
    </div>
  );
}
